import React, { useEffect, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { photoApi } from '../api/photoApi';
import { Photo } from '../types';
import { useProfile } from '../context/ProfileContext';
import { distanceUnitLabel, toDisplayDistance } from '../constants/units';

const PAGE_SIZE = 10;

const statusSeverity: Record<string, 'success' | 'warning' | 'info' | 'danger'> = {
  Pending: 'warning',
  Analyzing: 'info',
  Analyzed: 'success',
  NeedsRetake: 'warning',
  Error: 'danger',
};

const statusIcons: Record<string, string> = {
  Pending: 'pi-clock',
  Analyzing: 'pi-spin pi-spinner',
  Analyzed: 'pi-check-circle',
  NeedsRetake: 'pi-camera',
  Error: 'pi-exclamation-triangle',
};

const LibraryPage: React.FC = () => {
  const { profile } = useProfile();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [first, setFirst] = useState(0);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await photoApi.getAll(profile.userId);
      setPhotos(data.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()));
      setFirst(0);
    } catch {
      // silently handle; could add toast
    } finally {
      setLoading(false);
    }
  }, [profile.userId]);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const handleDelete = (photo: Photo) => {
    confirmDialog({
      message: `Delete photo "${photo.fileName}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-trash',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        await photoApi.delete(photo.id);
        setPhotos((prev) => {
          const next = prev.filter((p) => p.id !== photo.id);
          setFirst((f) => Math.min(f, Math.max(0, (Math.ceil(next.length / PAGE_SIZE) - 1) * PAGE_SIZE)));
          return next;
        });
      },
    });
  };

  const handleViewDetails = (photo: Photo) => {
    setSelectedPhoto(photo);
    setDialogVisible(true);
  };

  const handleAnalyze = async (photo: Photo) => {
    try {
      const updated = await photoApi.analyze(photo.id);
      setPhotos((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch {
      // best-effort; ignore
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 0' }}>
        <ProgressSpinner />
        <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading your shots...</p>
      </div>
    );
  }

  return (
    <div>
      <ConfirmDialog />

      <div className="hero-banner">
        <h1>📸 Shot Library</h1>
        <p>Review your uploaded shots and AI caddie recommendations</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          outlined
          style={{ borderRadius: 50, borderColor: '#22c55e', color: '#16a34a' }}
          onClick={loadPhotos}
        />
      </div>

      {photos.length === 0 ? (
        <div className="card-surface empty-state">
          <i className="pi pi-camera" />
          <h3>No shots yet!</h3>
          <p>Upload your first golf shot to get started with your Digital Caddie.</p>
        </div>
      ) : (
        <>
          <div className="photo-grid">
            {photos.slice(first, first + PAGE_SIZE).map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                measurementSystem={profile.measurementSystem}
                onDelete={handleDelete}
                onDetails={handleViewDetails}
                onAnalyze={handleAnalyze}
              />
            ))}
          </div>
          {photos.length > PAGE_SIZE && (
            <Paginator
              first={first}
              rows={PAGE_SIZE}
              totalRecords={photos.length}
              onPageChange={(e: PaginatorPageChangeEvent) => setFirst(e.first)}
              style={{ marginTop: '1rem', background: 'transparent', border: 'none' }}
            />
          )}
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        header={selectedPhoto?.fileName ?? 'Shot Details'}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '90vw', maxWidth: 560 }}
        modal
        closable
      >
        {selectedPhoto && <PhotoDetailPanel photo={selectedPhoto} measurementSystem={profile.measurementSystem} />}
      </Dialog>
    </div>
  );
};

const PhotoCard: React.FC<{ photo: Photo; measurementSystem: 'imperial' | 'metric'; onDelete: (p: Photo) => void; onDetails: (p: Photo) => void; onAnalyze: (p: Photo) => void }> = ({
  photo, measurementSystem, onDelete, onDetails, onAnalyze
}) => (
  <div className="photo-card">
    <div className="photo-card-image">
      {photo.blobUrl && photo.blobUrl.startsWith('http') ? (
        <img src={photo.blobUrl} alt={photo.fileName} />
      ) : (
        <i className="pi pi-image no-image-icon" aria-hidden="true" />
      )}
    </div>
    <div className="photo-card-body">
      <div className="photo-card-title" title={photo.fileName}>{photo.fileName}</div>
      <div className="photo-card-meta">
        {new Date(photo.uploadedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
        {photo.distanceToHoleYards ? ` · ${toDisplayDistance(photo.distanceToHoleYards, measurementSystem).toFixed(0)} ${distanceUnitLabel(measurementSystem)}` : ''}
      </div>
      <Tag
        severity={statusSeverity[photo.status] ?? 'info'}
        icon={`pi ${statusIcons[photo.status] ?? 'pi-info-circle'}`}
        value={photo.status}
        style={{ borderRadius: 50 }}
      />
    </div>

    {photo.analysis && (
      <div className="photo-card-analysis">
        {photo.analysis.recommendedClub && (
          <div className="analysis-item">
            <i className="pi pi-star" aria-hidden="true" />
            <span><strong>Club:</strong> {photo.analysis.recommendedClub}</span>
          </div>
        )}
        {photo.analysis.overallRecommendation && (
          <div className="analysis-item">
            <i className="pi pi-comment" aria-hidden="true" />
            <span>{photo.analysis.overallRecommendation}</span>
          </div>
        )}
      </div>
    )}

    <div style={{ display: 'flex', gap: '0.5rem', padding: '0 1rem 1rem' }}>
      <Button
        icon="pi pi-bolt"
        label="Caddie"
        size="small"
        style={{ flex: 1, borderRadius: 50, background: '#22c55e', border: 'none' }}
        aria-label="Analyze shot"
        onClick={() => onAnalyze(photo)}
      />
      <Button
        icon="pi pi-search"
        label="Details"
        outlined
        size="small"
        style={{ flex: 1, borderRadius: 50, borderColor: '#22c55e', color: '#16a34a' }}
        onClick={() => onDetails(photo)}
      />
      <Button
        icon="pi pi-trash"
        severity="danger"
        outlined
        size="small"
        style={{ borderRadius: 50 }}
        aria-label="Delete photo"
        onClick={() => onDelete(photo)}
      />
    </div>
  </div>
);

const PhotoDetailPanel: React.FC<{ photo: Photo; measurementSystem: 'imperial' | 'metric' }> = ({ photo, measurementSystem }) => (
  <div>
    {photo.description && <p style={{ marginBottom: '1rem', color: '#475569' }}>{photo.description}</p>}

    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
      <tbody>
        <DetailRow label="Status" value={photo.status} />
        <DetailRow label="Uploaded" value={new Date(photo.uploadedAt).toLocaleString()} />
        {photo.distanceToHoleYards !== undefined && (
          <DetailRow label="Distance to hole" value={`${toDisplayDistance(photo.distanceToHoleYards, measurementSystem).toFixed(0)} ${distanceUnitLabel(measurementSystem)}`} />
        )}
        {photo.location?.holeNumber !== undefined && (
          <DetailRow label="Hole #" value={String(photo.location.holeNumber)} />
        )}
        {photo.location?.courseName && (
          <DetailRow label="Course" value={photo.location.courseName} />
        )}
      </tbody>
    </table>

    {photo.analysis && (
      <div style={{ marginTop: '1.25rem' }}>
        <h4 style={{ color: '#16a34a', marginBottom: '0.75rem', fontWeight: 700 }}>🏌️ Caddie Analysis</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <tbody>
            {photo.analysis.recommendedClub && <DetailRow label="Recommended Club" value={photo.analysis.recommendedClub} />}
            {photo.analysis.stanceAdvice && <DetailRow label="Stance" value={photo.analysis.stanceAdvice} />}
            {photo.analysis.gripAdvice && <DetailRow label="Grip" value={photo.analysis.gripAdvice} />}
            {photo.analysis.clubFaceOrientation && <DetailRow label="Club Face" value={photo.analysis.clubFaceOrientation} />}
            {photo.analysis.swingSpeedRecommendation && <DetailRow label="Swing Speed" value={photo.analysis.swingSpeedRecommendation} />}
            {photo.analysis.estimatedSwingDistanceYards !== undefined && (
              <DetailRow label="Est. Distance" value={`${toDisplayDistance(photo.analysis.estimatedSwingDistanceYards, measurementSystem).toFixed(0)} ${distanceUnitLabel(measurementSystem)}`} />
            )}
            {photo.analysis.ballLieDescription && <DetailRow label="Ball Lie" value={photo.analysis.ballLieDescription} />}
            {photo.analysis.overallRecommendation && <DetailRow label="Overall Advice" value={photo.analysis.overallRecommendation} />}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <tr style={{ borderBottom: '1px solid #d1fae5' }}>
    <td style={{ padding: '0.5rem 0.25rem', fontWeight: 700, color: '#374151', width: '40%' }}>{label}</td>
    <td style={{ padding: '0.5rem 0.25rem', color: '#475569' }}>{value}</td>
  </tr>
);

export default LibraryPage;
